
import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Map, Marker } from 'pigeon-maps';

export default function MapLogin({ selectedLocation }) {
  const [open, setOpen] = useState(true);
  const [city, setCity] = useState('');

  // Fetch the city name using OpenStreetMap's reverse geocoding
  useEffect(() => {
    async function fetchCity() {
      if (selectedLocation) {
        const { latitude, longitude } = selectedLocation;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const cityName = data.address?.city || data.address?.town || data.address?.village || 'Unknown Location';
          setCity(cityName);
        } catch (error) {
          console.error('Error fetching city name:', error);
          setCity('Unable to determine location');
        }
      }
    }

    fetchCity();
  }, [selectedLocation]);

  if (open === false) {
    window.location.reload();
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-gradient-to-br from-white via-gray-50 to-indigo-50/40 backdrop-blur-sm" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-6xl h-full md:h-[80vh] bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Close button */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close map"
            className="absolute top-4 right-4 z-30 inline-flex items-center justify-center rounded-full bg-white/80 text-gray-700 hover:bg-white p-2 shadow"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="flex h-full flex-col md:flex-row">
            {/* Left pane: information */}
            <div className="md:w-1/3 w-full p-6 bg-gradient-to-b from-white to-gray-50 flex flex-col gap-4">
              <h2 className="text-2xl font-bold text-gray-900">Employee Location</h2>
              <p className="text-sm text-gray-600">Nearest Town</p>
              <p className="text-lg font-medium text-gray-800">{city}</p>

              <div className="mt-4">
                <p className="text-xs text-gray-500">Latitude</p>
                <p className="text-sm text-gray-700">{selectedLocation.latitude}</p>
                <p className="text-xs text-gray-500 mt-2">Longitude</p>
                <p className="text-sm text-gray-700">{selectedLocation.longitude}</p>
              </div>

              <div className="mt-auto">
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(`${selectedLocation.latitude}, ${selectedLocation.longitude}`);
                  }}
                  className="w-full rounded-md bg-indigo-600 text-white py-2 px-3 text-sm font-medium hover:bg-indigo-500"
                >
                  Copy coordinates
                </button>
              </div>
            </div>

            {/* Map pane */}
            <div className="md:w-2/3 w-full h-80 md:h-full relative">
              <Map
                center={[selectedLocation.latitude, selectedLocation.longitude]}
                zoom={13}
                width="100%"
                height="100%"
              >
                <Marker anchor={[selectedLocation.latitude, selectedLocation.longitude]} />
              </Map>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
